import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Lightweight pointer-based signature pad. Produces a PNG data URL.
 * No external library.
 */
export default function SignaturePad({ value, onChange, label = "Firma", height = 160 }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(!value);

  // Fit the canvas to its container with devicePixelRatio for crisp strokes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "#111827";
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return { x: pt.clientX - rect.left, y: pt.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    if (empty) setEmpty(false);
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    try {
      const data = canvasRef.current.toDataURL("image/png");
      onChange?.(data);
    } catch { /* intentionally ignored: toDataURL may fail if canvas is tainted */ }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange?.("");
  };

  return (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <Button type="button" variant="ghost" size="sm" onClick={clear} className="h-7 gap-1.5 text-xs" data-testid="signature-clear-button">
          <Eraser className="h-3.5 w-3.5" /> Limpiar
        </Button>
      </div>
      <div className={cn("relative rounded-md border border-dashed border-border bg-background", empty && "bg-surface-2") }>
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          style={{ width: "100%", height, touchAction: "none" }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
            Firma aquí con el mouse o el dedo
          </div>
        )}
      </div>
    </div>
  );
}
